import { useCallback, useEffect, useState } from "react";
import { consentApi } from "@/api/services";
import { useAuth } from "@/store/auth";
import type { ConsentPatch, ConsentStatus, CustomerConsent } from "@/api/consent-types";
import { isMarketingOptedIn } from "@/lib/marketing-consent";

export function useConsent() {
  const { isLoggedIn } = useAuth();
  const [consent, setConsent] = useState<CustomerConsent | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!isLoggedIn) return;
    setLoading(true);
    setError(null);
    try {
      setConsent(await consentApi.getMe());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load preferences");
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => { void load(); }, [load]);

  const optInMarketing = useCallback(async (optIn: boolean) => {
    setSaving(true);
    try {
      setConsent(await consentApi.optInMarketing(optIn));
    } finally {
      setSaving(false);
    }
  }, []);

  const setChannel = useCallback(
    async (channel: "whatsapp" | "email" | "push" | "sms", status: ConsentStatus) => {
      setSaving(true);
      try {
        const patch: ConsentPatch = { [channel]: status };
        setConsent(await consentApi.patch(patch));
      } finally {
        setSaving(false);
      }
    }, []);

  const withdrawAll = useCallback(async () => {
    setSaving(true);
    try {
      setConsent(await consentApi.patch({
        whatsapp: "opted_out", push: "opted_out", email: "opted_out", sms: "opted_out",
      }));
    } finally {
      setSaving(false);
    }
  }, []);

  const marketingOptedIn = consent ? isMarketingOptedIn(consent) : false;

  return { consent, loading, saving, error, reload: load, optInMarketing, setChannel, withdrawAll, marketingOptedIn };
}
